import type { FillDay, FillRoute, FillTank } from "./api";
import { dayTitle, shiftDay } from "./driverRoutes";
import { fillBand, fillVar, OVER_BAND } from "./fill";
import type { SortColumn } from "./sort";

export const BAND_COUNT = 21;
export const RING_LIMIT = 500;

/** The band of a fill share: 5% bands, then one band for a tank at or above full. */
export function bandOf(fill: number): number {
  return Math.min(Math.max(Math.floor(fill * 20 + 1e-9), 0), BAND_COUNT - 1);
}

/** The colour scheme band (U-22) of a 5% bar. Each scheme edge is a multiple of 5%, so a bar has one colour. */
export const barBand = (band: number) => (band >= BAND_COUNT - 1 ? OVER_BAND : fillBand(band * 5));

export const barColour = (band: number) => fillVar(barBand(band));

/** The top edge of a 5% bar: a fill of exactly 100% is in the "full or over" bar, so the last 5% bar ends at 99%. */
const topEdge = (band: number) => (band >= BAND_COUNT - 2 ? 99 : (band + 1) * 5);

export function bandLabel(band: number): string {
  return band >= BAND_COUNT - 1 ? "Full or over" : `${band * 5}–${topEdge(band)}%`;
}

export const pctText = (fill: number | null) => (fill === null ? "–" : `${Math.round(Math.min(fill, 1) * 100)}%`);

const tanksText = (count: number) => `${count.toLocaleString("en-US")} ${count === 1 ? "container" : "containers"}`;

/** "312 tanks; 41 at or over 90%; 7 at or over full." */
export function summaryText(found: Pick<FillDay, "tanks" | "high_share">): string {
  const high = found.tanks.filter((tank) => tank.fill >= found.high_share).length;
  const full = found.tanks.filter((tank) => tank.fill >= 1).length;
  return `${tanksText(found.tanks.length)}; ${high.toLocaleString("en-US")} at or over ${Math.round(found.high_share * 100)}%; ${full.toLocaleString("en-US")} at or over full.`;
}

export function bandText(band: number, count: number): string {
  return `${bandLabel(band)}: ${tanksText(count)}`;
}

/** The days that the view shows: today and each day up to the last day. */
export function dayOptions(today: string, last: string): { day: string; label: string }[] {
  const found: { day: string; label: string }[] = [];
  for (let day = today; day <= last && found.length < 31; day = shiftDay(day, 1)) found.push({ day, label: dayTitle(day, today) });
  return found;
}

const trucksText = (count: number) => `${count} ${count === 1 ? "truck" : "trucks"}`;

export function sourceText(found: Pick<FillDay, "source" | "routes">): string {
  const trucks = trucksText(found.routes.length);
  if (found.source === "routes") return `Saved routes: ${trucks}.`;
  if (found.source === "mixed") {
    const proposed = found.routes.filter((route) => route.proposed).length;
    return `Saved routes: ${trucksText(found.routes.length - proposed)}. The newest proposed plan adds ${trucksText(proposed)} with no saved route.`;
  }
  if (found.source === "plan") return `No saved route on this day. The map shows the newest proposed plan: ${trucks}.`;
  return "No route on this day.";
}

/** The tanks on no route at or above the share, fullest first, at most `limit` of them. */
export function rings(tanks: readonly FillTank[], share: number, limit: number = RING_LIMIT): { shown: FillTank[]; total: number } {
  const over = tanks.filter((tank) => tank.truck === null && tank.fill >= share && tank.lat !== null && tank.lng !== null);
  over.sort((a, b) => b.fill - a.fill || a.number.localeCompare(b.number));
  return { shown: over.slice(0, limit), total: over.length };
}

export interface RouteRow {
  truck: string;
  colour: number;
  proposed: boolean;
  stops: number;
  high: number;
  mean: number | null;
}

/** One line for each truck: the stops, the stops at or above the share, and the mean fill. */
export function routeRows(routes: readonly FillRoute[], share: number): RouteRow[] {
  return routes.map((route) => {
    const fills = route.stops.flatMap((stop) => (stop.fill === null ? [] : [stop.fill]));
    return {
      truck: route.truck,
      colour: route.colour,
      proposed: route.proposed,
      stops: route.stops.length,
      high: fills.filter((fill) => fill >= share).length,
      mean: fills.length ? fills.reduce((sum, fill) => sum + fill, 0) / fills.length : null,
    };
  });
}

/** The top of the count axis: 1, 2 or 5 times a power of ten, at or above the largest count. */
export function axisTop(counts: readonly number[]): number {
  const most = Math.max(1, ...counts);
  const step = 10 ** Math.floor(Math.log10(most));
  for (const factor of [1, 2, 5, 10]) if (factor * step >= most) return factor * step;
  return 10 * step;
}

export const CHART_WIDTH = 640;
const CHART_MIN_WIDTH = 280;

/** The view box width of the chart: its CSS width up to 640, so that the 12 px axis text is not smaller on a phone. */
export function chartWidth(measured: number): number {
  return measured > 0 ? Math.round(Math.min(Math.max(measured, CHART_MIN_WIDTH), CHART_WIDTH)) : CHART_WIDTH;
}

/** A fill range of the chart in 5% bars, both ends included. */
export interface FillRange {
  lo: number;
  hi: number;
}

export const FILL_PARAM = "fill";
const OVER = "over";

/** The range of two bars in any order, each clamped to the chart. */
export function snapRange(a: number, b: number): FillRange {
  const clamp = (band: number) => Math.min(Math.max(Math.round(band), 0), BAND_COUNT - 1);
  const [x, y] = [clamp(a), clamp(b)];
  return { lo: Math.min(x, y), hi: Math.max(x, y) };
}

/** The bar under an x position of the chart, in the units of its view box. */
export function bandAtX(x: number, left: number, step: number): number {
  return Math.min(Math.max(Math.floor((x - left) / step), 0), BAND_COUNT - 1);
}

/** "75-100" is 75% to 99%; "90-over" also holds each container at or over full. */
export function formatRange(range: FillRange): string {
  return `${range.lo * 5}-${range.hi >= BAND_COUNT - 1 ? OVER : (range.hi + 1) * 5}`;
}

export function parseRange(text: string | null): FillRange | null {
  const found = /^(\d{1,3})-(\d{1,3}|over)$/.exec(text ?? "");
  if (!found) return null;
  const from = Number(found[1]);
  const to = found[2] === OVER ? BAND_COUNT * 5 : Number(found[2]);
  if (from % 5 || to % 5 || from > 100 || to <= from || (found[2] !== OVER && to > 100)) return null;
  return { lo: from / 5, hi: to / 5 - 1 };
}

export const readRange = (search: string) => parseRange(new URLSearchParams(search).get(FILL_PARAM));

/** The search part of the URL with the range, or without it when there is no range. Other parameters stay. */
export function writeRange(search: string, range: FillRange | null): string {
  const params = new URLSearchParams(search);
  if (range) params.set(FILL_PARAM, formatRange(range));
  else params.delete(FILL_PARAM);
  const text = params.toString();
  return text ? `?${text}` : "";
}

export const inRange = (range: FillRange, fill: number) => {
  const band = bandOf(fill);
  return band >= range.lo && band <= range.hi;
};

export function rangeLabel(range: FillRange): string {
  if (range.lo >= BAND_COUNT - 1) return "Full or over";
  return `${range.lo * 5}% to ${range.hi >= BAND_COUNT - 1 ? "full or over" : `${topEdge(range.hi)}%`}`;
}

/** "Selected: 75% to 99% · 9 containers". */
export const selectedText = (range: FillRange, count: number) => `Selected: ${rangeLabel(range)} · ${tanksText(count)}`;

export const selectTanks = (tanks: readonly FillTank[], range: FillRange) => tanks.filter((tank) => inRange(range, tank.fill));

/** The options of the "from" and "to" lists: the edges of the 5% bars. */
export const fromOptions = () => Array.from({ length: BAND_COUNT }, (_, band) => ({ band, label: `${band * 5}%` }));
export const toOptions = () => Array.from({ length: BAND_COUNT }, (_, band) => ({ band, label: band >= BAND_COUNT - 1 ? "Full or over" : `${topEdge(band)}%` }));

/** The next visit of a container: the API value, or the shown day for a container on a route of that day (an older API). */
export const nextVisit = (tank: FillTank, day: string): string | null => (tank.next_visit !== undefined ? tank.next_visit : tank.truck ? day : null);

export const fillColumns = (day: string): SortColumn<FillTank>[] => [
  { key: "number", label: "Account", value: (tank) => tank.number },
  { key: "name", label: "Name", value: (tank) => tank.name ?? null },
  { key: "fill", label: "Fill", value: (tank) => tank.fill, first: "desc", num: true },
  { key: "truck", label: "Truck", value: (tank) => tank.truck },
  { key: "next", label: "Next visit", value: (tank) => nextVisit(tank, day) },
];

/** The fullest first, then by account number: the order before the user sorts. */
export const byFill = (tanks: readonly FillTank[]) => [...tanks].sort((a, b) => b.fill - a.fill || a.number.localeCompare(b.number));

export const TABLE_LIMIT = 500;

/** The tanks on no route that the map shows: the rings, and with a range also the selected ones, at most `limit` of each. */
export function looseTanks(tanks: readonly FillTank[], share: number, range: FillRange | null, limit: number = RING_LIMIT): FillTank[] {
  const shown = rings(tanks, share, limit).shown;
  if (!range) return shown;
  const ids = new Set(shown.map((tank) => tank.account_id));
  const picked = byFill(tanks.filter((tank) => tank.truck === null && tank.lat !== null && tank.lng !== null && !ids.has(tank.account_id) && inRange(range, tank.fill)));
  return [...shown, ...picked.slice(0, limit)];
}
